// PainDot — tiny coloured circle that surfaces whether an exercise loads
// any muscle the user has marked in their pain map. Lifted from
// apps/mobile/src/bodymap/PainDot.tsx (2026-08-17) so web-rnw can wire the
// same optional-slot DI contract (hostDeps.ts's PainDot/usePainRiskLabel,
// ExercisePicker's PainDot prop, ExerciseSwapPicker's PainDot/
// usePainRiskLabel) that mobile already fills — same shape as BodyMap/
// PainMapPicker's own lifts, injected `painMap` instead of a mobile-only
// ProfileContext read.
//
// Rendering rule: nothing when the user has no pain marked OR the
// exercise doesn't touch any painful muscle. Colour from
// PAIN_SEVERITY_COLOURS via exercisePainSeverity.

import { StyleSheet, View } from "react-native";
import { useTranslation } from "react-i18next";

import {
  PAIN_SEVERITY_COLOURS,
  exercisePainSeverity,
  painRiskSide,
  type Exercise,
  type PainMap,
} from "@heraion/shared";

import type { TFn } from "../i18nTypes";

export type PainDotProps = {
  exercise: Exercise;
  painMap: PainMap | undefined;
  size?: number;
};

// What the dot SAYS, separated from the dot itself so a row that sets its own
// accessibilityLabel can fold this in instead of silently swallowing it.
// (A parent label collapses the whole subtree for assistive tech, so the dot's
// own label disappears the moment an ancestor gets one — EQ-1 hit exactly that
// and the pain warning went quiet on every swap-picker row.)
//
// Plain-function core (no hook) so a host that already has `t` from its own
// top-level useTranslation() can build a per-row label getter without
// calling a hook inside a list's renderItem (rules-of-hooks — renderItem is
// invoked per item, not a stable component instance). `usePainRiskLabel`
// below is the hook convenience wrapper for hosts that ARE a row component
// (e.g. ExerciseSwapPickerRow).
export function painRiskLabel(
  exercise: Exercise,
  painMap: PainMap | undefined,
  t: TFn,
): string | null {
  const risk = exercisePainSeverity(exercise, painMap);
  if (!risk) return null;
  const severity = t(`bodymap.painSeverity.${risk.severity}`);
  // PG-14: a one-sided pain says which side, so "right shoulder" never
  // reads as "shoulder".
  const side = painRiskSide(risk);
  if (!side) return t("bodymap.painRiskA11y", { severity });
  return t("bodymap.painRiskSideA11y", {
    severity,
    side: t(`bodymap.painSideOnly.${side}`),
  });
}

export function usePainRiskLabel(
  exercise: Exercise,
  painMap: PainMap | undefined,
): string | null {
  const { t } = useTranslation();
  return painRiskLabel(exercise, painMap, t);
}

export function PainDot({ exercise, painMap, size = 8 }: PainDotProps) {
  const painLabel = usePainRiskLabel(exercise, painMap);
  const risk = exercisePainSeverity(exercise, painMap);
  if (!risk) return null;
  return (
    <View
      accessibilityRole="image"
      accessibilityLabel={painLabel ?? undefined}
      style={[
        styles.dot,
        {
          width: size,
          height: size,
          borderRadius: size / 2,
          backgroundColor: PAIN_SEVERITY_COLOURS[risk.severity],
        },
      ]}
    />
  );
}

const styles = StyleSheet.create({
  dot: {
    flexShrink: 0,
  },
});
